"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger, DrawerFooter } from "@/components/ui/drawer";
import { Menu, Phone, X } from "lucide-react";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="1535" className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/30 animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium tracking-wide text-foreground/70 transition-colors hover:text-primary">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted/30 animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="flex items-center py-3 text-lg font-bold tracking-tight text-foreground transition-colors hover:text-primary border-b border-border/20 last:border-0" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-xl shadow-lg border-border/10" : "bg-background")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <Link to="/HomePage" className="flex-shrink-0 z-50">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573441/img/angliakeys-logo.png" alt="AngliaKeys Auto Locksmith" className="h-9 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden md:flex items-center justify-center flex-1 px-8">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          <Button asChild className="hidden md:inline-flex bg-primary hover:bg-primary/90 text-primary-foreground font-extrabold rounded-full shadow-xl shadow-primary/30 animate-pulse hover:animate-none transition-all uppercase tracking-tighter">
            <Link to="tel:07123456789">
              <Phone className="mr-2 h-4 w-4 fill-current" />
              24/7 EMERGENCY CALL
            </Link>
          </Button>

          {}
          <div className="md:hidden">
            <SafeHydrate><Drawer direction="right" open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10 text-foreground">
                  <Menu className="h-7 w-7" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DrawerTrigger>
              <DrawerContent className="h-full rounded-none border-l border-border/20 bg-background/95 backdrop-blur-lg">
                <DrawerHeader className="flex justify-between items-center border-b border-border/10 p-4">
                  <DrawerTitle className="text-left">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573441/img/angliakeys-logo.png" alt="AngliaKeys" className="h-8 w-auto" />
                  </DrawerTitle>
                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon" className="text-foreground/60">
                      <X className="h-6 w-6" />
                      <span className="sr-only">Close</span>
                    </Button>
                  </DrawerClose>
                </DrawerHeader>
                
                <div className="flex-1 overflow-y-auto">
                  <MenuProvider menu_id="16">
                    <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
                  </MenuProvider>
                </div>

                <DrawerFooter className="border-t border-border/10 p-4">
                  <Button asChild className="w-full bg-primary font-extrabold text-primary-foreground text-lg h-14 rounded-full shadow-lg shadow-primary/20">
                    <Link to="tel:07123456789">
                      <Phone className="mr-2 h-5 w-5 fill-current" />
                      24/7 EMERGENCY CALL
                    </Link>
                  </Button>
                </DrawerFooter>
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>
        </div>
      </div>
    </header>;
}